/* Analytics helper — initializes PostHog (if configured AND consented) and
   exposes a unified `lfTrack(eventName, properties)` that fires both to
   PostHog and to our in-house /api/track/event endpoint (promoter-aware).

   Respects GDPR consent: PostHog is NOT initialized until the user grants
   `analytics` consent. The in-house tracker is ALSO gated — events are not
   logged if the user has actively opted out of analytics. First-touch
   promoter capture via ?ref=<code> is considered strictly_necessary
   (revenue-attribution for a service the user signed up for), so the
   /api/track/click call is always made.
*/
import posthog from "posthog-js";
import axios from "axios";
import { hasConsent, hasDecided, onConsentChange } from "./consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
let POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.posthog.com";

let posthogReady = false;
let runtimeConfigFetched = false;

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
let gaReady = false;
let lastGaPath = null;

function maybeInitGoogleAnalytics() {
  if (!GA_MEASUREMENT_ID || gaReady) return;
  if (!hasConsent("analytics")) return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
    }
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true, send_page_view: false });
    gaReady = true;
    sendGaPageView(window.location.pathname);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Google Analytics init failed:", e);
  }
}

function sendGaPageView(path) {
  if (!gaReady || !window.gtag) return;
  const p = path || window.location.pathname;
  if (p === lastGaPath) return;
  lastGaPath = p;
  window.gtag("event", "page_view", {
    page_path: p,
    page_location: window.location.href,
    page_title: document.title,
  });
}

async function fetchRuntimeConfig() {
  // Pull the PostHog public key + host from the backend so admins can rotate
  // it via /admin/settings without a frontend rebuild. Falls back to env vars.
  if (runtimeConfigFetched) return;
  runtimeConfigFetched = true;
  try {
    const res = await axios.get(`${API}/public/runtime-config`, { timeout: 5000 });
    if (res?.data?.POSTHOG_PROJECT_KEY) POSTHOG_KEY = res.data.POSTHOG_PROJECT_KEY;
    if (res?.data?.POSTHOG_HOST) POSTHOG_HOST = res.data.POSTHOG_HOST;
  } catch (_) { /* runtime config is best-effort */ }
}

function getOrCreateAnonymousId() {
  let id = localStorage.getItem("lf_anon_id");
  if (!id) {
    id = "anon_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem("lf_anon_id", id);
  }
  return id;
}

function getSessionId() {
  let id = sessionStorage.getItem("lf_session_id");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    sessionStorage.setItem("lf_session_id", id);
  }
  return id;
}

export function initAnalytics() {
  // 1) First-touch promoter capture: when URL has ?ref=<code>, store & ping.
  // This is revenue-attribution — considered necessary for the service.
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      const existing = localStorage.getItem("lf_promoter_code");
      if (!existing) {
        localStorage.setItem("lf_promoter_code", ref.trim().toLowerCase());
        localStorage.setItem("lf_promoter_touched_at", new Date().toISOString());
      }
      axios
        .get(`${API}/track/click`, {
          params: { ref, landing_url: window.location.href, referrer: document.referrer || "" },
          withCredentials: true,
        })
        .then((res) => {
          if (res?.data?.click_id) localStorage.setItem("lf_promoter_click", res.data.click_id);
        })
        .catch(() => {});
    }
  } catch (_) {}

  // 2) Pull runtime config (PostHog key/host) then maybe init PostHog.
  fetchRuntimeConfig().then(() => maybeInitPostHog());
  maybeInitGoogleAnalytics();

  // Re-evaluate analytics tooling on consent changes.
  onConsentChange(() => {
    maybeInitPostHog();
    maybeInitGoogleAnalytics();
  });
}

function maybeInitPostHog() {
  if (!POSTHOG_KEY || posthogReady) return;
  if (!hasConsent("analytics")) return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      respect_dnt: true,
    });
    posthogReady = true;
    const code = localStorage.getItem("lf_promoter_code");
    if (code) posthog.register({ promoter_code: code });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("PostHog init failed:", e);
  }
}

export function lfTrack(eventName, properties) {
  // Respect GDPR consent. If user has made a decision and opted out of
  // analytics, don't send. If they haven't decided yet, also don't send.
  if (!hasConsent("analytics")) return;
  const props = properties || {};
  // In-house (for promoter attribution + built-in funnel)
  try {
    axios.post(
      `${API}/track/event`,
      {
        event_name: eventName,
        properties: props,
        path: typeof window !== "undefined" ? window.location.pathname : null,
        referrer: typeof document !== "undefined" ? document.referrer : null,
        session_id: getSessionId(),
        anonymous_id: getOrCreateAnonymousId(),
      },
      { withCredentials: true }
    );
  } catch (_) {}
  // PostHog
  if (posthogReady && posthog) {
    try { posthog.capture(eventName, props); } catch (_) {}
  }
  // Google Analytics (page views are sent via sendGaPageView, not here)
  if (gaReady && window.gtag && eventName !== "$pageview") {
    try {
      const gaName = eventName.replace(/^\$/, "").replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 40);
      window.gtag("event", gaName, props);
    } catch (_) {}
  }
}

export function lfTrackPageView(path) {
  sendGaPageView(path);
  lfTrack("$pageview", { path: path || window.location.pathname });
}

export function lfIdentify(userId, properties) {
  if (posthogReady && posthog) {
    try { posthog.identify(userId, properties || {}); } catch (_) {}
  }
}

// Expose as window.lfTrack for non-React consumers
if (typeof window !== "undefined") {
  window.lfTrack = lfTrack;
  window.lfTrackPageView = lfTrackPageView;
  window.lfIdentify = lfIdentify;
}
